/**
 * Locale-aware date / number / currency formatters built on the platform's own
 * `Intl` — no formatting library (D-08). The active locale is passed in so the
 * same helpers work on web and mobile.
 */
/** A bare `YYYY-MM-DD`: a calendar date with no time and no zone in it. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * ⚠ A BARE `YYYY-MM-DD` IS A CALENDAR DATE, NOT AN INSTANT. `new Date('2025-12-31')` is midnight
 * UTC, and `Intl` then prints it in the reader's own zone, so anywhere west of UTC a due date of the
 * 31st reads as the 30th. The api hands out plain dates for due dates, entry dates and schedules,
 * so those are read and printed in UTC, where the date they name is the date they show. A full
 * timestamp is an instant and still prints in the reader's zone.
 */
export function formatDate(value, locale, options) {
    const opts = options ?? { dateStyle: 'medium' };
    if (typeof value === 'string' && CALENDAR_DATE.test(value)) {
        return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...opts }).format(new Date(`${value}T00:00:00Z`));
    }
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(locale, opts).format(date);
}
export function formatNumber(value, locale, options) {
    return new Intl.NumberFormat(locale, options).format(value);
}
export function formatCurrency(value, locale, currency = 'USD', options) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...options,
    }).format(value);
}
